// ===========================================
// Invoice amendment — shared display rules (2026-09-26)
// ===========================================
// One place for "can this invoice be amended?" and the checkout-lock wording,
// so the invoice list and the invoice page cannot drift. The BACKEND gate
// (services/invoice/amend) is the real rule; this only decides what to show.

import type { Invoice, InvoiceSnapshot as Snapshot } from '../api/invoices.api';
import { effectiveStartWeek, formatInSchoolTz, lessonWeekWindow, termWeekCount } from './dateUtils';
import { storedClassWeeks, type ClassWeeksRow } from './classWeeks';
import { livePayments } from './invoiceDisplay';

type AmendableFields = Pick<Invoice, 'status' | 'Payment' | 'stripeInvoiceId'>;
type CheckoutFields = Pick<Invoice, 'checkoutStartedAt' | 'checkoutExpiresAt'>;

/**
 * SENT or OVERDUE, no LIVE payment row (#642: as the server counts them; voiding the only
 * payment re-opens amendment), not a Stripe subscription invoice
 * (Andrew, 2026-09-25: amendable only until the first payment).
 */
export function canAmendInvoice(invoice: AmendableFields): boolean {
  return (
    (invoice.status === 'SENT' || invoice.status === 'OVERDUE') &&
    livePayments(invoice).length === 0 &&
    !invoice.stripeInvoiceId
  );
}

/** A family is on Stripe's checkout page for this invoice right now. */
export function isCheckoutLive(invoice: CheckoutFields, now: Date = new Date()): boolean {
  return !!invoice.checkoutExpiresAt && new Date(invoice.checkoutExpiresAt).getTime() > now.getTime();
}

const clock = (instant: string, timeZone: string) =>
  formatInSchoolTz(instant, timeZone, { hour: 'numeric', minute: '2-digit' });

/**
 * "The family started paying online at 3:42 pm. Amending unlocks at 4:13 pm,
 * or sooner if they finish or leave checkout." — times in the SCHOOL's zone.
 */
/** `action` names what is locked: "Amending" (default) or "Voiding" (the void button shares the lock). */
export function checkoutLockMessage(invoice: CheckoutFields, timeZone: string, action = 'Amending'): string {
  const started = invoice.checkoutStartedAt
    ? `The family started paying online at ${clock(invoice.checkoutStartedAt, timeZone)}.`
    : 'The family is paying this invoice online right now.';
  const unlocks = invoice.checkoutExpiresAt
    ? ` ${action} unlocks at ${clock(invoice.checkoutExpiresAt, timeZone)}, or sooner if they finish or leave checkout.`
    : '';
  return started + unlocks;
}


/**
 * What an amendment changed, as short human sentences for the history table.
 * Lines are matched by description (the summary string IS a line's identity
 * here — weeks are not persisted per line).
 */
const KIND_LABEL = { TUITION: 'Tuition', PRODUCT: 'Product', OTHER: 'Other' } as const;

export function describeAmendmentChanges(before: Snapshot, after: Snapshot): string[] {
  const changes: string[] = [];
  const money = (v: string) => `$${Number(v).toFixed(2)}`;
  const line = (i: Snapshot['items'][number]) => `${i.quantity} × ${money(i.unitPrice)}`;

  // A line's identity is its description + product + package, and a repeated
  // identical line keeps its own slot (#2, #3…) so duplicates never collapse.
  const keyed = (items: Snapshot['items']) => {
    const seen = new Map<string, number>();
    return new Map(
      items.map((i) => {
        const base = `${i.description}|${i.productId ?? ''}|${i.pricingPackageId ?? ''}`;
        const n = (seen.get(base) ?? 0) + 1;
        seen.set(base, n);
        return [`${base}#${n}`, i] as const;
      })
    );
  };
  const beforeByKey = keyed(before.items);
  const afterByKey = keyed(after.items);

  for (const [key, a] of afterByKey) {
    const b = beforeByKey.get(key);
    if (!b) changes.push(`Added “${a.description}” (${line(a)})`);
    else if (b.quantity !== a.quantity || b.unitPrice !== a.unitPrice) {
      changes.push(`Changed “${a.description}”: ${line(b)} → ${line(a)}`);
    }
    // #372: a line's kind (snapshots before 2026-10-15 have none: never a change).
    if (b && b.kind && a.kind && b.kind !== a.kind) {
      changes.push(`Kind of “${a.description}”: ${KIND_LABEL[b.kind]} → ${KIND_LABEL[a.kind]}`);
    }
  }
  for (const [key, b] of beforeByKey) {
    if (!afterByKey.has(key)) changes.push(`Removed “${b.description}” (${line(b)})`);
  }
  // Discounts this amendment ADDED or REMOVED — matched by row id (snapshots
  // carry it since 2026-09-28), falling back to name|value|% for older ones.
  const discountKey = (d: Snapshot['discounts'][number]) => d.id ?? `${d.name}|${d.value}|${d.isPercent}`;
  const beforeDiscounts = new Set(before.discounts.map(discountKey));
  const afterDiscounts = new Set(after.discounts.map(discountKey));
  for (const d of after.discounts) {
    if (!beforeDiscounts.has(discountKey(d))) {
      // #625: and what it covers, unless everything (snapshots before #625 carry no label).
      const covers = d.coverageLabel && d.coverageLabel !== 'everything' ? `, covers ${d.coverageLabel}` : '';
      changes.push(`Discount added “${d.name}” (−${money(d.calculatedAmount)}${covers})`);
    }
  }
  for (const d of before.discounts) {
    if (!afterDiscounts.has(discountKey(d))) {
      changes.push(`Discount removed “${d.name}” (was −${money(d.calculatedAmount)})`);
    }
  }
  if ((before.description ?? '').trim() !== (after.description ?? '').trim()) {
    changes.push('Description changed');
  }
  if (before.dueDate !== after.dueDate) {
    const day = (key: string) => key.split('-').reverse().join('/'); // YYYY-MM-DD -> dd/mm/yyyy
    changes.push(`Due date ${day(before.dueDate)} → ${day(after.dueDate)}`);
  }
  return changes;
}


// ===========================================
// Mid-term start week: the amend prompt's wording (2026-09-30)
// ===========================================
// A class window (or a student's start week) changed, so a sent invoice MAY be wrong. The
// server cannot tell which invoice billed which class (a line carries no lesson id), so
// every sentence here says "may" / "check". Nothing is auto-amended.

/** "week 7", "weeks 5–10". */
export function weeksLabel(first: number, last: number): string {
  return first === last ? `week ${first}` : `weeks ${first}–${last}`;
}

/** "week 4", "weeks 1, 3, 5", "no weeks" — a class's chosen weeks (class weeks, 2026-10-16). */
export function weekListLabel(weeks: number[]): string {
  if (weeks.length === 0) return 'no weeks';
  return weeks.length === 1 ? `week ${weeks[0]}` : `weeks ${weeks.join(', ')}`;
}

/**
 * What a class runs, in words: its chosen weeks inside its window ("weeks 1, 3, 5"), or — with
 * no chosen list (`null`: every week) — the window itself ("weeks 1–10").
 */
export function classRunsLabel(window: { first: number; last: number }, weeks?: number[] | null): string {
  if (weeks == null) return weeksLabel(window.first, window.last);
  return weekListLabel([...weeks].filter((w) => w >= window.first && w <= window.last).sort((a, b) => a - b));
}

/**
 * The chosen-weeks list the amend wording should name, or null for "every week of the window".
 * Every plain type (INDIVIDUAL / GROUP / BAND): a CUSTOM list only. An Every-week BAND's stored
 * or previewed list is derived and can lag the switch (the preview route returns the stored
 * CUSTOM list until the save), which read "now runs weeks 3, 6, 9 (was weeks 3, 6, 9)" for a
 * CUSTOM → Every week change (/qa class weeks Phase 7 M1). HYBRID (or no type): null.
 */
export function classWeeksForWording(
  lessonType: string | undefined,
  patternType: string | null | undefined,
  weeks: number[] | null | undefined
): number[] | null {
  if (lessonType !== 'INDIVIDUAL' && lessonType !== 'GROUP' && lessonType !== 'BAND') return null;
  return patternType === 'CUSTOM' ? (weeks ?? []) : null;
}

/** What changed about a class or a student, for the amend prompt and the amend-dialog banner. */
export type AmendChange =
  | {
      kind: 'class';
      lessonName: string;
      newWindow: { first: number; last: number };
      previousWindow: { first: number; last: number };
      /** The chosen weeks after / before the save (`classWeeksForWording`); absent or null = every week. */
      newWeeks?: number[] | null;
      previousWeeks?: number[] | null;
      /**
       * Set ONLY when invoices came from terms other than the one the windows/weeks above describe
       * (a multi-term edit): every such term, in order. The sentence then names the terms instead
       * of one term's "now … (was …)", which would be wrong for the others (/qa whole feature B-M2).
       */
      termNames?: string[];
    }
  | {
      kind: 'student';
      studentName: string;
      lessonName: string;
      newStartWeek: number;
      previousStartWeek: number;
    };

/** The sentence that opens the prompt: what changed, and the "was …". */
/** "Term 4 2026", "Term 4 2026 and Term 1 2027", "A, B and C". */
export function termNamesLabel(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function amendChangeSentence(change: AmendChange): string {
  if (change.kind === 'class' && change.termNames && change.termNames.length > 0) {
    return `${change.lessonName} changed in ${termNamesLabel(change.termNames)}.`;
  }
  // A weeks-only change keeps the window, so the window alone would read "now runs weeks 1–10
  // (was weeks 1–10)" (/qa class weeks Phase 3 M1): name the chosen weeks when there are any.
  return change.kind === 'class'
    ? `${change.lessonName} now runs ${classRunsLabel(change.newWindow, change.newWeeks)} ` +
        `(was ${classRunsLabel(change.previousWindow, change.previousWeeks)}).`
    : `${change.studentName} now starts ${change.lessonName} in week ${change.newStartWeek} ` +
        `(was week ${change.previousStartWeek}).`;
}

/** What the amend dialog's banner knows: the class (and student) the admin came from. */
export type AmendBannerContext =
  | { kind: 'class'; lessonName: string; window: { first: number; last: number }; weeks?: number[] | null }
  | { kind: 'student'; studentName: string; lessonName: string; startWeek: number };

/**
 * "Sent 12/10/2026 for Term 4. Piano Foundation now runs weeks 5–10 — check the lines below."
 * `sentOn` is already a school-zone dd/mm/yyyy (or null when the invoice has no sent date).
 */
export function amendBannerText(
  context: AmendBannerContext,
  sentOn: string | null,
  termName: string | null | undefined,
  isDraft = false
): string {
  // A draft has not been sent: it is being EDITED before it goes out (D-b, 2026-10-01).
  const opening = isDraft ? 'Draft' : sentOn ? `Sent ${sentOn}` : 'Sent';
  const lead = `${opening}${termName ? ` for ${termName}` : ''}. `;
  const what =
    context.kind === 'class'
      ? `${context.lessonName} now runs ${classRunsLabel(context.window, context.weeks)}`
      : `${context.studentName} now starts ${context.lessonName} in week ${context.startWeek}`;
  return `${lead}${what} — check the lines below.`;
}

/** Why a deep-linked invoice cannot be amended right now, for the page-level alert. */
export function amendUnavailableReason(
  invoice: Pick<Invoice, 'status' | 'Payment' | 'stripeInvoiceId'> & Partial<CheckoutFields>,
  timeZone: string,
  canWrite: boolean,
  checkoutLive: boolean
): string {
  if (!canWrite) return "You don't have permission to amend invoices.";
  if (invoice.stripeInvoiceId) return "This invoice is managed by Stripe and can't be amended here.";
  if (livePayments(invoice).length > 0 || invoice.status === 'PAID' || invoice.status === 'PARTIALLY_PAID') {
    // #642 D15: credit applied at send locks amendment like a payment, and Take off re-opens it.
    if ((invoice.Payment ?? []).some((p) => !p.voidedAt && p.method === 'ACCOUNT_CREDIT')) {
      return 'Account credit has been applied to this invoice, so it can no longer be amended. Take the account credit off first.';
    }
    return 'A payment has been recorded against this invoice, so it can no longer be amended. Issue a credit or a new invoice instead.';
  }
  if (invoice.status !== 'SENT' && invoice.status !== 'OVERDUE') {
    return 'Only a sent invoice can be amended.';
  }
  if (checkoutLive) return checkoutLockMessage(invoice as CheckoutFields, timeZone);
  return 'This invoice cannot be amended right now.';
}

/** The slice of a loaded class the amend banner reads. */
export interface AmendBannerLesson {
  name: string;
  /** With `LessonType` and `LessonTermWeeks`: the class's chosen weeks name the banner (class weeks). */
  termId?: string;
  LessonType?: { type: string } | null;
  LessonTermWeeks?: ClassWeeksRow[] | null;
  startWeek?: number | null;
  endWeek?: number | null;
  Term: { startDate: string; endDate: string };
  LessonEnrollment?: Array<{
    studentId: string;
    startWeek?: number | null;
    Student: { firstName: string; lastName: string };
  }>;
}

/**
 * The banner's subject: the class window, or (with a `studentId` whose enrolment is on the
 * class) that student's effective start. An unknown student falls back to the class sentence
 * rather than to nothing.
 */
export function buildAmendBannerContext(
  lesson: AmendBannerLesson,
  studentId: string | null
): AmendBannerContext {
  const weeks = termWeekCount(lesson.Term.startDate, lesson.Term.endDate);
  const enrolment = studentId
    ? lesson.LessonEnrollment?.find((e) => e.studentId === studentId)
    : undefined;
  if (enrolment) {
    return {
      kind: 'student',
      studentName: `${enrolment.Student.firstName} ${enrolment.Student.lastName}`,
      lessonName: lesson.name,
      startWeek: effectiveStartWeek(lesson, enrolment),
    };
  }
  // The stored chosen weeks (a fortnightly class's list), read through the one FE rule. A BAND
  // with no CUSTOM list, or any HYBRID, reads null: the window alone describes it.
  const chosen = lesson.termId
    ? storedClassWeeks({
        ...lesson,
        termId: lesson.termId,
        LessonType: lesson.LessonType ?? null,
        LessonTermWeeks: lesson.LessonTermWeeks ?? null,
      })
    : null;
  return {
    kind: 'class',
    lessonName: lesson.name,
    window: lessonWeekWindow(lesson, weeks),
    ...(chosen !== null && { weeks: chosen }),
  };
}
